import * as React from 'react';
import { Formik, Field, useFormikContext } from 'formik';

import { Button } from 'components/bootstrap';
import { Modal } from 'common/components';
import { useModalContext } from 'common/contexts';
import { Col, Row } from 'common/components/layout-styles';
import { useGetEventProcedure } from 'security-app/hooks/useEventProceduresAPI';
import type { CreateProcedurePayloadType } from 'security-app/hooks/api/eventProceduresAPI.types';

import {
  EventProcedureBaseForm,
  ShareFormFormikField,
  validations,
  useFormActions,
} from '../plugin/EventProcedureForm';
import { INIT_EVENT_PROCEDURE } from '../plugin/EventProcedureForm/utils';

type Props = {
  onClose: () => void;
  eventProcedureId?: string | undefined;
  isCreate?: boolean;
};

function Buttons({ isEdit = false, saving }: { isEdit?: boolean; saving: boolean }) {
  const { setModal, setEntity } = useModalContext();
  const { resetForm, isValid, dirty, submitForm } = useFormikContext<CreateProcedurePayloadType>();

  const onCancel = React.useCallback(() => {
    resetForm();
    setEntity(undefined);
    setModal(undefined);
  }, [resetForm, setEntity, setModal]);

  return (
    <Row $justify="flex-end">
      <Button type="reset" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="button" bsStyle="primary" onClick={submitForm} disabled={!isValid || !dirty || saving}>
        {isEdit ? 'Save' : 'Create'} Event Procedure
      </Button>
    </Row>
  );
}

function EventProcedureModal({ onClose, eventProcedureId = '', isCreate = false }: Props) {
  const { eventProcedure, loadingEventProcedure } = useGetEventProcedure(eventProcedureId);
  const { handleSubmit, saving } = useFormActions();

  const onSubmit = React.useCallback(
    (inValues: CreateProcedurePayloadType) => {
      handleSubmit({ inValues, eventProcedureId });
      onClose();
    },
    [eventProcedureId, handleSubmit, onClose],
  );

  if (loadingEventProcedure) return null;

  return (
    <Formik initialValues={eventProcedure || INIT_EVENT_PROCEDURE} validate={validations} onSubmit={onSubmit}>
      <Modal
        show
        onClose={onClose}
        maxWidth="1000px"
        title={`${eventProcedureId ? 'Edit' : 'Create'} Event Procedure`}
        buttons={<Buttons isEdit={!!eventProcedureId} saving={saving} />}>
        <Col>
          <EventProcedureBaseForm />
          {isCreate && (
            <Field
              name="share_request"
              id="share_request"
              component={ShareFormFormikField}
              description="Search for a User or Team to add as collaborator on this event procedure."
              entityType="event_procedure"
            />
          )}
        </Col>
      </Modal>
    </Formik>
  );
}

export default EventProcedureModal;
